import { DateTime } from "luxon";
import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MoonLoader, PropagateLoader } from "react-spinners";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";

import { openStoredArtifact } from "artifactsSlice";
import { resetChat } from "chatBotEventsSlice";
import {
  SkillInfo,
  clearDeleteSkillError,
  selectSkill,
  startDeleteSkill,
} from "components/skills/skillsSlice";
import { setPendingSkill } from "components/skills/skillsState";
import { hide, show } from "components/Home/homeVisibilitySlice";
import { toggleSideNav } from "shared/components/SideNav/SideNavToggleButton/toggleSideNav";
import { RatingStars } from "shared/components/RatingStars/RatingStars";
import api from "shared/functions/api";
import { useAppUserId } from "shared/functions/appJwt";
import { dispatchToWebChat } from "shared/functions/webChatStore";

import { setTextToSearch } from "../Cards/Card/cardTextSlice";
import { userSendPrompt } from "./userSendPromptSlicer";
import RefreshIcon from "./RefreshIcon";

import "./SideNav.css";

interface SubMenuProps {
  menu: any;
  favorites?: any[];
  onFavoritesChange?: () => void;
}

interface DropDownProps {
  id: number;
  title: string;
  icon: string;
  showIcon: boolean;
  openDropdown: number | null;
  hasOptions: boolean;
  onToggleDropdown: () => void;
}

const DropDown = ({
  id,
  title,
  icon,
  showIcon,
  openDropdown,
  hasOptions,
  onToggleDropdown,
}: DropDownProps) => {
  return (
    <>
      <span onClick={onToggleDropdown} className="sidenav-link">
        <div style={{ display: "flex" }}>
          {showIcon && <img src={icon} alt="recent-chat" />}
          <span className="sidenav-label">{title}</span>
        </div>
        <div>
          {openDropdown === id ? (
            <img
              src="static/images/icons/ArrowDown.png"
              alt=""
              className="arrow-up-icon"
            />
          ) : hasOptions ? (
            <img src="static/images/icons/ArrowDown.png" alt="" className="arrow-down-icon" />
          ) : null}
        </div>
      </span>
    </>
  );
};

const SubMenu = ({ menu }: SubMenuProps) => {
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [openChildDropdown, setOpenChildDropdown] = useState<number | null>(
    null
  );
  let dispatch = useDispatch();

  const toggleDropdown = (index: number) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const toggleChildDropdown = (index: number) => {
    setOpenChildDropdown(openChildDropdown === index ? null : index);
  };

  const formatDate = (date: string) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return DateTime.fromISO(date, { zone: timezone }).toFormat("dd/MM/yyyy");
  };

  const getHour = (date: string) => {
    return DateTime.fromISO(date).toFormat("hh:mm");
  };

  const getConversation = async (sessionId: string) => {
    const jwt = localStorage.getItem("jwt") || "";
    let newToken = await api.fetchConversationHistory(sessionId, jwt);
    localStorage.setItem("newToken", JSON.stringify(newToken));
    // Al reconectar, App.tsx manda este id al bot (evento
    // luca/resumeConversation) para que la conversación continúe
    // donde se quedó, no solo se muestre el historial.
    localStorage.setItem("resumeSessionId", sessionId);
    dispatch(hide());
    dispatch(toggleSideNav(false));
    dispatch(resetChat(true));
  };

  return (
    <>
      <DropDown
        id={menu.id}
        title={menu.title}
        icon={menu.icon}
        showIcon={true}
        hasOptions={+menu.options.length >= 1}
        openDropdown={openDropdown}
        onToggleDropdown={() => toggleDropdown(1)}
      />
      {openDropdown === menu.id &&
        menu.options.map((subMenu: any, index: number) => {
          return (
            <>
              <p className="dropdown-link">
                <DropDown
                  id={index}
                  title={formatDate(subMenu.date)}
                  icon=""
                  showIcon={false}
                  hasOptions={+subMenu.conversations.length >= 1}
                  openDropdown={openChildDropdown}
                  onToggleDropdown={() => toggleChildDropdown(index)}
                />
              </p>
              {openChildDropdown === index &&
                subMenu.conversations.map((conversation: any) => {
                  return (
                    <>
                      <p
                        className="dropdown-link"
                        onClick={() => getConversation(conversation.id)}
                      >
                        <span className="sidenav-label">
                          {/* {getHour(conversation.timestamp)} */}
                          {conversation.prompt}
                        </span>
                      </p>
                    </>
                  );
                })}
            </>
          );
        })}
    </>
  );
};

const SubMenuItem = ({ menu, favorites = [], onFavoritesChange }: SubMenuProps) => {
  const originalOptions = menu.options;
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [options, setOptions] = useState(originalOptions)
  let dispatch = useDispatch();

  const toggleDropdown = (index: number) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const handleClick = (text: string) => {
    dispatch(setTextToSearch(text));
    dispatch(toggleSideNav(false));
  }

  const handleStarClick = async (e: React.MouseEvent, promptText: string) => {
    e.stopPropagation();
    const jwtToken = localStorage.getItem('jwt') || '';
    const alreadySaved = favorites.some((f: any) => f.prompt_text === promptText);
    if (alreadySaved) return;
    try {
      await api.saveFavorite(jwtToken, promptText);
      onFavoritesChange?.();
      window.dispatchEvent(new CustomEvent('favorites-updated'));
    } catch (err) {
      console.error('Error saving favorite:', err);
    }
  };


  const onSearchPrompt = (e: any) => {
    const filterOptions = e.target.value.length === 0 ? originalOptions : originalOptions.filter((prompt: any) => {
      if (prompt.original_question.toLowerCase()
          .includes(e.target.value.toLowerCase())) { return prompt; }
      })
    
    setOptions(filterOptions)
  }

  return (
    <>
      <DropDown
        id={menu.id}
        title={menu.title}
        icon={menu.icon}
        showIcon={true}
        hasOptions={+menu.options.length >= 1}
        openDropdown={openDropdown}
        onToggleDropdown={() => toggleDropdown(1)}
      />
      { openDropdown === menu.id && 
        <div className="searcher-prompts-wrapper">
          <textarea
            id="searcher-prompts"
            placeholder="Search prompt..."
            onChange={(e) => onSearchPrompt(e)}
            rows={1}
            cols={25}
          ></textarea>

          <button id="searcher-prompts-button"></button>
        </div>
      }
      {openDropdown === menu.id &&
        options.map((option: any, index: number) => {
          return (
            <>
              <p
                className="dropdown-link"
                onClick={() => handleClick(option.original_question)}
              >
                <span className="sidenav-label">
                  {option.original_question}
                </span>
                <button
                  className={`fav-star-btn${favorites.some((f: any) => f.prompt_text === option.original_question) ? ' fav-star-btn--saved' : ''}`}
                  onClick={(e) => handleStarClick(e, option.original_question)}
                  title="Save to favorites"
                >
                  ★
                </button>
              </p>
            </>
          );
        })}
    </>
  );
};

const FavoritesSection = ({ favorites, onDelete }: { favorites: any[]; onDelete: (id: string) => void }) => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const handleClick = (promptText: string) => {
    dispatch(setTextToSearch(promptText));
    dispatch(toggleSideNav(false));
  };

  return (
    <>
      <span onClick={() => setOpen(prev => !prev)} className="sidenav-link">
        <div style={{ display: 'flex' }}>
          <span className="fav-section-icon">&#9733;</span>
          <span className="sidenav-label">My favorite Prompts</span>
        </div>
        <div>
          {open ? (
            <img src="static/images/icons/ArrowDown.png" alt="" className="arrow-up-icon" />
          ) : (
            <img src="static/images/icons/ArrowDown.png" alt="" className="arrow-down-icon" />
          )}
        </div>
      </span>
      {open && (
        favorites.length === 0 ? (
          <p className="fav-empty">No favorite prompts saved yet. Click ★ on any prompt to save it.</p>
        ) : (
          favorites.map((fav: any) => (
            <p key={fav.id} className="dropdown-link fav-item" onClick={() => handleClick(fav.prompt_text)}>
              <span className="sidenav-label">{fav.prompt_text}</span>
              <button
                className="fav-delete-btn"
                onClick={(e) => { e.stopPropagation(); onDelete(fav.id); }}
                title="Remove from favorites"
              >
                &#x2715;
              </button>
            </p>
          ))
        )
      )}
    </>
  );
};

const ArtifactIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1.6" y="2.6" width="12.8" height="10.8" rx="1.6" stroke="#343434" strokeWidth="1.1" fill="none"/>
    <line x1="6.2" y1="2.6" x2="6.2" y2="13.4" stroke="#343434" strokeWidth="1.1"/>
    <line x1="8.2" y1="6" x2="12.4" y2="6" stroke="#343434" strokeWidth="1.1" strokeLinecap="round"/>
    <line x1="8.2" y1="8.6" x2="11.2" y2="8.6" stroke="#343434" strokeWidth="1.1" strokeLinecap="round"/>
  </svg>
);

// Biblioteca de artifacts del usuario (/api/artifacts): un renglón por
// conversación con el título del artifact y, en chico, la fecha de su
// versión más reciente. Al hacer clic se trae el HTML de esa versión y
// se abre en el panel lateral — puede ser de otra conversación, así que
// el panel NO lo marca como artifact de la conversación en curso
// (openStoredArtifact).
const ArtifactsSection = ({
  artifacts,
  loading,
}: {
  artifacts: any[];
  loading: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [openingId, setOpeningId] = useState("");
  const dispatch = useDispatch();
  // Sesión del artifact que el panel tiene abierto: si el usuario
  // elige uno de la conversación en la que ya está, no hay nada que
  // retomar (recargar la conversación perdería el chat en curso).
  const panelSessionId = useSelector((state: any) => state.artifacts.sessionId);
  const panelIsCurrent = useSelector(
    (state: any) => state.artifacts.belongsToConversation
  );

  const formatModified = (value: string) => {
    if (!value) return "";
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = DateTime.fromISO(value, { zone: timezone });
    return date.isValid ? date.toFormat("dd/MM/yyyy HH:mm") : "";
  };

  // Abrir un artifact abre TAMBIÉN su conversación: es el mismo flujo
  // que hacer clic en un chat de "Recent Chats" (historial visual +
  // evento de resume para que el bot continúe donde se quedó), pero
  // dejando dicho en localStorage qué versión mostrar — App.tsx la pasa
  // como preferredArtifactId al conectar y el panel abre ESA, no la
  // última de la conversación.
  const handleOpen = async (artifact: any) => {
    if (openingId || !artifact?.artifactId) return;
    setOpeningId(artifact.artifactId);
    try {
      const jwt = localStorage.getItem("jwt") || "";
      const sameConversation =
        panelIsCurrent &&
        !!artifact.sessionId &&
        artifact.sessionId === panelSessionId;
      if (artifact.sessionId && !sameConversation) {
        const newToken = await api.fetchConversationHistory(
          artifact.sessionId,
          jwt
        );
        localStorage.setItem("newToken", JSON.stringify(newToken));
        localStorage.setItem("resumeSessionId", artifact.sessionId);
        localStorage.setItem("openArtifactId", artifact.artifactId);
        // La sesión viaja junto al id: si este resume no llega a
        // conectar, la elección no debe aplicarse a otra conversación.
        localStorage.setItem("openArtifactSession", artifact.sessionId);
        dispatch(hide());
        dispatch(toggleSideNav(false));
        dispatch(resetChat(true));
        return;
      }
      // Ya se está en esa conversación, o el artifact no tiene sesión
      // (generado fuera del chat): sólo se muestra en el panel.
      const detail = await api.fetchArtifactById(jwt, artifact.artifactId);
      dispatch(
        openStoredArtifact({ ...detail, sessionId: artifact.sessionId })
      );
      dispatch(toggleSideNav(false));
    } catch (err) {
      console.error("Error opening artifact:", err);
    } finally {
      setOpeningId("");
    }
  };

  return (
    <>
      <span onClick={() => setOpen((prev) => !prev)} className="sidenav-link">
        <div style={{ display: "flex" }}>
          <ArtifactIcon />
          <span className="sidenav-label">Artifacts</span>
        </div>
        <div>
          {open ? (
            <img src="static/images/icons/ArrowDown.png" alt="" className="arrow-up-icon" />
          ) : (
            <img src="static/images/icons/ArrowDown.png" alt="" className="arrow-down-icon" />
          )}
        </div>
      </span>
      {open &&
        (loading && artifacts.length === 0 ? (
          <p className="fav-empty">Loading artifacts...</p>
        ) : artifacts.length === 0 ? (
          <p className="fav-empty">
            No artifacts yet. Ask LUCA for a dashboard or an interactive
            report and it will show up here.
          </p>
        ) : (
          artifacts.map((artifact: any) => (
            <p
              key={artifact.artifactId}
              className="dropdown-link artifact-item"
              title={artifact.title}
              onClick={() => handleOpen(artifact)}
            >
              <span className="artifact-item-title">{artifact.title}</span>
              <span className="artifact-item-meta">
                {openingId === artifact.artifactId
                  ? "Opening..."
                  : formatModified(artifact.updatedAt)}
                {artifact.versionCount > 1 && ` · v${artifact.version}`}
              </span>
            </p>
          ))
        ))}
    </>
  );
};

const SkillIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.6 1.8L3.4 9h4l-0.8 5.2L12.6 7h-4l0.8-5.2z" stroke="#343434" strokeWidth="1.1" strokeLinejoin="round" fill="none"/>
  </svg>
);

// Skills que el usuario creó con /new-skill (guardadas en Cosmos; sólo
// él las ve). Salen del mismo catálogo que el menú "/" (event
// "luca/skills" del bot), así que una skill recién guardada aparece
// aquí sin recargar. Al hacer clic queda elegida en el chat igual que
// desde el menú "/": chip azul pendiente y channelData.skill en el
// siguiente mensaje (con o sin texto).
// Sin skills en el bot (ENABLE_SKILLS apagado) la sección no se muestra.
const SkillsSection = () => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const catalog: SkillInfo[] = useSelector((state: any) => state.skills.catalog);
  const loaded: boolean = useSelector((state: any) => state.skills.catalogLoaded);
  const deleting: string | null = useSelector((state: any) => state.skills.deleting);
  const deleteError: string = useSelector((state: any) => state.skills.deleteError);
  // Skill cuya × se pulsó: el renglón pide confirmación ahí mismo (no
  // window.confirm, que Teams bloquea dentro de sus pestañas).
  const [confirming, setConfirming] = useState<string | null>(null);
  const mySkills = catalog.filter((skill) => skill.mine);

  // Si el bot no contesta al borrado, el renglón no se queda en
  // "Deleting..." para siempre.
  useEffect(() => {
    if (!deleting) return;
    const timer = setTimeout(() => dispatch(clearDeleteSkillError()), 20000);
    return () => clearTimeout(timer);
  }, [deleting, dispatch]);

  if (loaded && catalog.length === 0) return null;

  const handleClick = (skill: SkillInfo) => {
    if (deleting === skill.name) return;
    // Mismo efecto que chooseSkill (webChat.tsx) al elegirla en el
    // menú "/": el chip la muestra y el siguiente mensaje la activa.
    dispatch(selectSkill(skill));
    setPendingSkill(skill.name);
    dispatch(toggleSideNav(false));
    document.getElementById("sendbox-textarea")?.focus();
  };

  // Ya confirmado: el bot la borra de Cosmos y contesta con el catálogo
  // actualizado (event "luca/skills", ver App.tsx).
  const handleDelete = (e: React.MouseEvent, skill: SkillInfo) => {
    e.stopPropagation();
    setConfirming(null);
    dispatch(startDeleteSkill(skill.name));
    dispatchToWebChat({
      type: "WEB_CHAT/SEND_EVENT",
      payload: { name: "luca/deleteSkill", value: { name: skill.name } },
    });
  };

  return (
    <>
      <span onClick={() => setOpen((prev) => !prev)} className="sidenav-link">
        <div style={{ display: "flex" }}>
          <SkillIcon />
          <span className="sidenav-label">Skills</span>
        </div>
        <div>
          {open ? (
            <img src="static/images/icons/ArrowDown.png" alt="" className="arrow-up-icon" />
          ) : (
            <img src="static/images/icons/ArrowDown.png" alt="" className="arrow-down-icon" />
          )}
        </div>
      </span>
      {open &&
        (!loaded ? (
          <p className="fav-empty">Loading skills...</p>
        ) : mySkills.length === 0 ? (
          <p className="fav-empty">
            No skills yet. Type /new-skill in the chat to create one and it
            will show up here.
          </p>
        ) : (
          <>
            {deleteError && <p className="skill-delete-error">{deleteError}</p>}
            {mySkills.map((skill) => (
              <p
                key={skill.name}
                className="dropdown-link artifact-item skill-item"
                title={skill.description || skill.summary}
                onClick={() => handleClick(skill)}
              >
                <span className="skill-item-header">
                  <span className="artifact-item-title">{skill.title}</span>
                  {deleting !== skill.name && confirming !== skill.name && (
                    <button
                      className="fav-delete-btn skill-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirming(skill.name);
                      }}
                      title="Delete skill"
                      disabled={!!deleting}
                    >
                      &#x2715;
                    </button>
                  )}
                </span>
                {confirming === skill.name ? (
                  <span
                    className="skill-delete-confirm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span>Delete this skill? This can't be undone.</span>
                    <span className="skill-delete-actions">
                      <button
                        className="skill-delete-yes"
                        onClick={(e) => handleDelete(e, skill)}
                      >
                        Delete
                      </button>
                      <button
                        className="skill-delete-no"
                        onClick={() => setConfirming(null)}
                      >
                        Cancel
                      </button>
                    </span>
                  </span>
                ) : (
                  <>
                    {skill.summary && (
                      <span className="skill-item-summary">{skill.summary}</span>
                    )}
                    <span className="artifact-item-meta">
                      {deleting === skill.name ? "Deleting..." : skill.command}
                    </span>
                  </>
                )}
              </p>
            ))}
          </>
        ))}
    </>
  );
};

// Sólo estos correos pueden ver y cambiar el modelo; el resto queda
// fijo en GPT 5.1 y el selector no se muestra. La comparación es contra
// el user_id del JWT (preferred_username de Entra ID) normalizado a
// minúsculas, así que la lista se guarda ya normalizada.
const MODEL_SELECTOR_ALLOWED_EMAILS = new Set(
  [
    'antonio.alvarezlopez@cemex.com',
    'julio.sandoval@derevo.com.mx',
  ].map((email) => email.trim().toLowerCase())
);

const MODELS = [
  { name: 'GPT 5.1', disabled: false },
  { name: 'Cortex', disabled: false },
];

const ModelSelector = () => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string>(() => {
    const stored = localStorage.getItem('selectedModel');
    // Un valor guardado que ya no exista en MODELS cae al default.
    return stored && MODELS.some((model) => model.name === stored)
      ? stored
      : 'GPT 5.1';
  });

  const handleSelect = (model: { name: string; disabled: boolean }) => {
    if (model.disabled) return;
    setSelected(model.name);
    localStorage.setItem('selectedModel', model.name);
    setOpen(false);
  };

  return (
    <>
      <span onClick={() => setOpen(prev => !prev)} className="sidenav-link">
        <div style={{ display: 'flex' }}>
          <img src="static/images/icons/Stars.svg" alt="model" style={{ width: 16, height: 16 }} />
          <span className="sidenav-label">Model</span>
        </div>
        <div>
          {open ? (
            <img src="static/images/icons/ArrowDown.png" alt="" className="arrow-up-icon" />
          ) : (
            <img src="static/images/icons/ArrowDown.png" alt="" className="arrow-down-icon" />
          )}
        </div>
      </span>
      {open && (
        <>
          <p className="model-selected-label">Selected: <strong>{selected}</strong></p>
          {MODELS.map((model) => (
            <p
              key={model.name}
              className={`dropdown-link model-option${selected === model.name ? ' model-option--selected' : ''}${model.disabled ? ' model-option--disabled' : ''}`}
              onClick={() => handleSelect(model)}
            >
              <span className="sidenav-label">{model.name}</span>
              {model.disabled && <span className="model-soon">Soon</span>}
              {selected === model.name && !model.disabled && <span className="model-check">✓</span>}
            </p>
          ))}
        </>
      )}
    </>
  );
};

const TEMPERATURES = [
  { name: 'Low', disabled: true },
  { name: 'Medium', disabled: true },
  { name: 'High', disabled: true },
];

const ThermometerIcon = ({ color = '#B0B0B0' }: { color?: string }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6.5" y="2" width="3" height="8" rx="1.5" stroke={color} strokeWidth="1.1" fill="none"/>
    <circle cx="8" cy="12" r="2" stroke={color} strokeWidth="1.1" fill="none"/>
    <line x1="8" y1="7" x2="8" y2="10" stroke={color} strokeWidth="1.1" strokeLinecap="round"/>
  </svg>
);

const TemperatureSelector = () => {
  const [open, setOpen] = useState(false);
  const [isThinkEnabled, setIsThinkEnabled] = useState<boolean>(() => {
    return localStorage.getItem('thinkEnabled') === 'true';
  });
  const selected = 'Medium';

  const toggleThink = () => {
    setIsThinkEnabled((prev) => {
      const next = !prev;
      localStorage.setItem('thinkEnabled', String(next));
      return next;
    });
  };

  return (
    <>
      <span onClick={() => setOpen(prev => !prev)} className="sidenav-link">
        <div style={{ display: 'flex' }}>
          <ThermometerIcon color={isThinkEnabled ? '#0074D4' : '#B0B0B0'} />
          <span className="sidenav-label">Thinking Temperature</span>
        </div>
        <span className={`think-status-pill ${isThinkEnabled ? 'think-status-pill--on' : 'think-status-pill--off'}`}>
          {isThinkEnabled ? 'On' : 'Off'}
        </span>
        <div>
          {open ? (
            <img src="static/images/icons/ArrowDown.png" alt="" className="arrow-up-icon" />
          ) : (
            <img src="static/images/icons/ArrowDown.png" alt="" className="arrow-down-icon" />
          )}
        </div>
      </span>
      {open && (
        <>
          <div className="think-toggle-wrapper">
            <button
              type="button"
              className={`think-toggle-btn ${isThinkEnabled ? 'think-toggle-btn--on' : 'think-toggle-btn--off'}`}
              onClick={toggleThink}
            >
              Think: {isThinkEnabled ? 'On' : 'Off'}
            </button>
          </div>
          <p className="model-selected-label">Selected: <strong>{selected}</strong></p>
          <p className="temperature-hint">Deeper thinking, longer processing time.</p>
          {TEMPERATURES.map((temp) => (
            <p
              key={temp.name}
              className={`dropdown-link model-option model-option--disabled${temp.name === selected ? ' model-option--selected' : ''}`}
            >
              <span className="sidenav-label">{temp.name}</span>
              <span className="model-soon">Soon</span>
            </p>
          ))}
        </>
      )}
    </>
  );
};

const Spinner = ({ loading }: any) => {
  const override: CSSProperties = {
    display: "block",
    margin: "0 auto",
    // borderColor: "#0074D4",
    borderColor: '#0074D4',
  };
  

  return (
    <div className="sweet-loading">
      <MoonLoader
        color="#0074D4"
        loading={loading}
        cssOverride={override}
        size={80}
        aria-label="Loading Spinner"
        data-testid="loader"
      />
    </div>
  );
}

export function SideNav() {
  const [chats, setChats] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [promptsHistory, setPromptsHistory] = useState<any[]>([]);
  const [favorites, setFavorites] = useState<any[]>([]);
  const [artifacts, setArtifacts] = useState<any[]>([]);
  const [loadingArtifacts, setLoadingArtifacts] = useState<boolean>(true);
  // Artifact mostrado en el panel: cuando cambia (el bot generó uno
  // nuevo) la biblioteca se recarga para que aparezca en la lista.
  const currentArtifactId = useSelector(
    (state: any) => state.artifacts.artifactId
  );
  // Correo del usuario en sesión. Se toma del JWT vía suscripción (NO
  // con una lectura de localStorage al montar): el login es asíncrono y
  // el token normalmente se escribe después de que el SideNav montó.
  const userId = useAppUserId();
  let sideNavValue = useSelector((state: any) => state.toggleSideNav.value);
  let whenUserSendPrompt = useSelector((state: any) => state.userSendPrompt.value);
  let dispatch = useDispatch();
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const canSelectModel =
    !!userId && MODEL_SELECTOR_ALLOWED_EMAILS.has(userId.trim().toLowerCase());

  // Usuarios sin permiso quedan siempre en GPT 5.1, aunque hubieran
  // elegido otro modelo antes de este cambio.
  useEffect(() => {
    if (userId && !canSelectModel) {
      localStorage.setItem('selectedModel', 'GPT 5.1');
    }
  }, [userId, canSelectModel]);

  const [minWidth, maxWidth, defaultWidth] = [200, 400, 230];
  const [width, setWidth] = useState(defaultWidth);
  const isResized = useRef(false);

  const refreshFavorites = useCallback(async () => {
    try {
      const jwt = localStorage.getItem("jwt") || "";
      const favs = await api.fetchFavorites(jwt);
      setFavorites(favs);
    } catch (err) {
      console.error("Error refreshing favorites:", err);
    }
  }, []);

  const refreshArtifacts = useCallback(async () => {
    // Sin JWT todavía (el login es asíncrono) no se llama: un 401 en
    // /api/* relanza el login de Entra ID y se perdería la sesión.
    const jwt = localStorage.getItem("jwt") || "";
    if (!jwt) return;
    try {
      const list = await api.fetchArtifacts(jwt);
      setArtifacts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error loading artifacts:", err);
    } finally {
      setLoadingArtifacts(false);
    }
  }, []);

  const handleDeleteFavorite = useCallback(async (favoriteId: string) => {
    try {
      const jwt = localStorage.getItem("jwt") || "";
      await api.deleteFavorite(jwt, favoriteId);
      setFavorites(prev => prev.filter((f: any) => f.id !== favoriteId));
    } catch (err) {
      console.error("Error deleting favorite:", err);
    }
  }, []);

  const handleClick = async () => {
    dispatch(userSendPrompt('New chat'))
    dispatch(toggleSideNav(false));
    // await getConversations();
    const jwt = localStorage.getItem("jwt") || "";
    let newToken = await api.fetchToken(jwt);
    localStorage.setItem("newToken", JSON.stringify(newToken));
    dispatch(show());
    dispatch(resetChat(true));
  };

  const getConversations = useCallback(async () => {
    try {
      const jwt = localStorage.getItem("jwt") || "";
      const conversations = await api.fetchConversationsIds(jwt);
      const allPromptsHistory = await api.fetchPromptsHistory(jwt);
      const dates = Object.keys(conversations);
      const conversationsByDate = [];
      setChats([]);
      setPromptsHistory([])
      for (const date of dates) {
        conversationsByDate.push({
          date: date,
          conversations: conversations[date],
        });
      }

      const result = Object.values(allPromptsHistory).flat();

      const seen = new Set();
      const uniquePromptsData = result.filter((item: any) => {
          const duplicate = seen.has(item.original_question);
          seen.add(item.original_question);
          return !duplicate;
      });

      setChats([
        {
          id: 1,
          title: "Recent Chats",
          icon: "static/images/icons/History.png",
          options: conversationsByDate,
        }
      ]);
      setPromptsHistory([
        {
          id: 1,
          title: "Prompts",
          icon: "static/images/icons/Prompt.png",
          options: uniquePromptsData,
        },
      ]);
      setLoadingData(false);
    } catch (error) {
      console.error("Login error:", error);
      setLoadingData(false);
    }
    // Load favorites independently so a failure doesn't block chats/prompts
    try {
      const jwt = localStorage.getItem("jwt") || "";
      const favs = await api.fetchFavorites(jwt);
      setFavorites(favs);
    } catch (favError) {
      console.error("Favorites error:", favError);
    }
  }, []);

  useEffect(() => {
    console.log('El estado someState ha cambiado:', whenUserSendPrompt);
    if (+whenUserSendPrompt.length > 0) {
      setLoadingData(true);
      setTimeout(async () => {
        await getConversations()
      }, whenUserSendPrompt === 'New chat'? 5000 : 15000);
    }
  }, [getConversations, whenUserSendPrompt, setLoadingData]);

  // Biblioteca de artifacts: al terminar el login (userId) y cada vez
  // que el panel muestra un artifact distinto (uno recién generado).
  useEffect(() => {
    if (!userId) return;
    refreshArtifacts();
  }, [userId, currentArtifactId, refreshArtifacts]);

  useEffect(() => {
    const onFavoritesUpdated = () => refreshFavorites();
    window.addEventListener('favorites-updated', onFavoritesUpdated);
    return () => window.removeEventListener('favorites-updated', onFavoritesUpdated);
  }, [refreshFavorites]);

  useEffect(() => {
    window.addEventListener("mousemove", (e) => {
      if (!isResized.current) {
        return;
      }

      setWidth((previousWidth) => {
        const newWidth = previousWidth + e.movementX / 2;

        const isWidthInRange = newWidth >= minWidth && newWidth <= maxWidth;

        return isWidthInRange ? newWidth : previousWidth;
      });
    });

    window.addEventListener("mouseup", () => {
      isResized.current = false;
    });
  }, []);


  return (
    <>
      <div
        id="sidenav"
        className={sideNavValue ? "show-sidenav" : "hide-sidenav"}
      >
        <div className="sidenav-content" style={{ width: `${width / 16}rem` }}>
          <ul>
            {/* Botón Refresh eliminado del menú lateral */}
            <li onClick={handleClick}>
              <div className="single-option">
                <img src="static/images/icons/Pencil.png" alt="new-chat" />
                <span>New Chat</span>
              </div>
            </li>
            {
              !loadingData && <>
                {chats.map((item: any, index: number) => {
                  return (
                    <li>
                      <SubMenu menu={item} key={index} />
                    </li>
                  );
                })}
                {promptsHistory.map((item: any, index: number) => {
                  return (
                    <li>
                      <SubMenuItem menu={item} key={index} favorites={favorites} onFavoritesChange={refreshFavorites} />
                    </li>
                  );
                })}
                <li>
                  <FavoritesSection favorites={favorites} onDelete={handleDeleteFavorite} />
                </li>
                <li>
                  <ArtifactsSection artifacts={artifacts} loading={loadingArtifacts} />
                </li>
                <li>
                  <SkillsSection />
                </li>
              </>
            }
            {canSelectModel && (
              <li>
                <ModelSelector />
              </li>
            )}
            <li style={{ display: 'none' }}>
              <TemperatureSelector />
            </li>
            <li>
              <a
                href="https://cemex.sharepoint.com/sites/LUCABot"
                target="_blank"
                rel="noopener noreferrer"
                className="sidenav-help-link"
              >
                <span className="sidenav-help-icon">?</span>
                <span>How to use it?</span>
              </a>
            </li>
          </ul>
          {/* Oculto: Rate your experience */}
          <div className="sidenav-rating-section" style={{ display: 'none' }}>
            <div className="sidenav-rating-item">
              <span className="sidenav-rating-label">Rate your experience</span>
              {userId ? <RatingStars userId={userId} /> : null}
            </div>
          </div>

          <div className="spinner-content">
            <Spinner loading={loadingData} />
          </div>
        </div>
        <div
          className="sidenav-border-cursor"
          onMouseDown={() => {
            isResized.current = true;
          }}
        />
      </div>
    </>
  );
}
